import { useState } from "react";
import { toast } from "sonner";
import { useCounties } from "@/shared/hooks/useMetaData";
import { useCreateGreenhouse } from "../../hooks/useGreenhouses";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DatePicker } from "@/components/ui/date-picker";
import { Field, FieldLabel } from "@/components/ui/field";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { Plus } from "lucide-react";
import { UserSearchSelect } from "@/shared/components/UserSearchSelect";
import { useConfirm } from "@/shared/contexts/confirm-context";

const STRUCTURE_TYPES = [
  "Glass",
  "Polycarbonate",
  "Polyethylene",
  "Shade Cloth",
  "Mixed",
];

const initialFormData = {
  greenhouseName: "",
  greenhouseCode: "",
  location: "",
  description: "",
  sizeSquareMeters: 0,
  structureType: "",
  maxCapacity: 0,
  hasIrrigation: false,
  hasClimateControl: false,
  hasAutomation: false,
  commissionedDate: new Date().toISOString().split("T")[0],
  managerUserId: "",
};

export const CreateGreenhouseDialog = () => {
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState(initialFormData);
  const [managerName, setManagerName] = useState("");
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});

  const createMutation = useCreateGreenhouse();
  const { data: counties, isLoading: countiesLoading } = useCounties();
  const confirm = useConfirm();

  const handleChange = (
    field: keyof typeof initialFormData,
    value: string | number | boolean,
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (fieldErrors[field]) {
      setFieldErrors((prev) => {
        const { [field]: _, ...rest } = prev;
        return rest;
      });
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFieldErrors({});

    if (!formData.managerUserId) {
      setFieldErrors({ managerUserId: ["Please select a manager"] });
      return;
    }

    const payload = {
      ...formData,
      commissionedDate: new Date(formData.commissionedDate).toISOString(),
    };

    confirm({
      title: "Create greenhouse?",
      description: `“${formData.greenhouseName}” will be added to the system.`,
      confirmLabel: "Create greenhouse",
      onConfirm: () =>
        createMutation
          .mutateAsync(payload as any)
          .then(() => {
            toast.success("Greenhouse created successfully!");
            setFormData(initialFormData);
            setManagerName("");
            setOpen(false);
          })
          .catch((error: any) => {
            const errorMessage = error.response?.data?.message || "Failed to create greenhouse";
            const validationErrors = error.response?.data?.errors;

            if (validationErrors) {
              setFieldErrors(validationErrors);
              // Combine all validation errors for toast display
              const errorMessages = Object.entries(validationErrors)
                .flatMap(([, messages]: [string, any]) =>
                  Array.isArray(messages) ? messages : [messages]
                );

              if (errorMessages.length > 0) {
                toast.error(errorMessages.join("\n"));
                throw error;
              }
            }

            toast.error(errorMessage);
            throw error;
          }),
    });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="gap-2">
          <Plus className="w-4 h-4" />
          New Greenhouse
        </Button>
      </DialogTrigger>
      <DialogContent 
        className="max-h-[90vh] overflow-y-auto overflow-x-hidden [&>*]:min-w-0 w-[95vw] sm:!max-w-[1400px]">
        <DialogHeader>
          <DialogTitle>Create New Greenhouse</DialogTitle>
          <DialogDescription>Add a new greenhouse to manage</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Basic Information</h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 [&>*]:min-w-0">
              <Field>
                <FieldLabel htmlFor="greenhouseName">
                  Greenhouse Name <span className="text-red-500">*</span>
                </FieldLabel>
                <Input
                  id="greenhouseName"
                  value={formData.greenhouseName}
                  onChange={(e) =>
                    handleChange("greenhouseName", e.target.value)
                  }
                  required
                  disabled={createMutation.isPending}
                />
                {fieldErrors.greenhouseName && (
                  <div className="text-red-500 text-xs mt-1">
                    {fieldErrors.greenhouseName.join(", ")}
                  </div>
                )}
              </Field>

              <Field>
                <FieldLabel htmlFor="greenhouseCode">
                  Greenhouse Code <span className="text-red-500">*</span>
                </FieldLabel>
                <Input
                  id="greenhouseCode"
                  value={formData.greenhouseCode}
                  onChange={(e) =>
                    handleChange("greenhouseCode", e.target.value)
                  }
                  placeholder="e.g., GH-001"
                  required
                  disabled={createMutation.isPending}
                />
                {fieldErrors.greenhouseCode && (
                  <div className="text-red-500 text-xs mt-1">
                    {fieldErrors.greenhouseCode.join(", ")}
                  </div>
                )}
              </Field>

              <Field>
                <FieldLabel htmlFor="location">
                  Location <span className="text-red-500">*</span>
                </FieldLabel>
                {countiesLoading ? (
                  <div className="h-10 bg-gray-200 rounded animate-pulse" />
                ) : (
                  <Select
                    value={formData.location}
                    onValueChange={(value) => handleChange("location", value)}
                    disabled={createMutation.isPending}
                  >
                    <SelectTrigger id="location" className="w-full">
                      <SelectValue placeholder="Select County" />
                    </SelectTrigger>
                    <SelectContent>
                      {counties?.map((county) => (
                        <SelectItem key={county.code} value={county.name}>
                          {county.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                {fieldErrors.location && (
                  <div className="text-red-500 text-xs mt-1">
                    {fieldErrors.location.join(", ")}
                  </div>
                )}
              </Field>

              <Field>
                <FieldLabel htmlFor="structureType">
                  Structure Type <span className="text-red-500">*</span>
                </FieldLabel>
                <Select
                  value={formData.structureType}
                  onValueChange={(value) =>
                    handleChange("structureType", value)
                  }
                  disabled={createMutation.isPending}
                >
                  <SelectTrigger id="structureType" className="w-full">
                    <SelectValue placeholder="Select Structure Type" />
                  </SelectTrigger>
                  <SelectContent>
                    {STRUCTURE_TYPES.map((type) => (
                      <SelectItem key={type} value={type}>
                        {type}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {fieldErrors.structureType && (
                  <div className="text-red-500 text-xs mt-1">
                    {fieldErrors.structureType.join(", ")}
                  </div>
                )}
              </Field>

              <Field className="col-span-2">
                <FieldLabel htmlFor="description">Description</FieldLabel>
                <textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => handleChange("description", e.target.value)}
                  disabled={createMutation.isPending}
                  placeholder="Details about this greenhouse"
                  className="flex min-h-20 w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
                />
              </Field>
            </div>
          </div>

          {/* Physical Specifications */}
          <div>
            <h3 className="text-lg font-semibold mb-4">
              Physical Specifications
            </h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 [&>*]:min-w-0">
              <Field>
                <FieldLabel htmlFor="sizeSquareMeters">
                  Size (Square Meters) <span className="text-red-500">*</span>
                </FieldLabel>
                <Input
                  id="sizeSquareMeters"
                  type="number"
                  step="0.01"
                  value={formData.sizeSquareMeters}
                  onChange={(e) =>
                    handleChange(
                      "sizeSquareMeters",
                      parseFloat(e.target.value) || 0,
                    )
                  }
                  required
                  disabled={createMutation.isPending}
                />
                {fieldErrors.sizeSquareMeters && (
                  <div className="text-red-500 text-xs mt-1">
                    {fieldErrors.sizeSquareMeters.join(", ")}
                  </div>
                )}
              </Field>

              <Field>
                <FieldLabel htmlFor="maxCapacity">
                  Max Capacity <span className="text-red-500">*</span>
                </FieldLabel>
                <Input
                  id="maxCapacity"
                  type="number"
                  step="1"
                  value={formData.maxCapacity}
                  onChange={(e) =>
                    handleChange("maxCapacity", parseInt(e.target.value) || 0)
                  }
                  required
                  disabled={createMutation.isPending}
                />
                {fieldErrors.maxCapacity && (
                  <div className="text-red-500 text-xs mt-1">
                    {fieldErrors.maxCapacity.join(", ")}
                  </div>
                )}
              </Field>
            </div>
          </div>

          {/* Features */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Features</h3>
            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="hasIrrigation"
                  checked={formData.hasIrrigation}
                  onCheckedChange={(checked) =>
                    handleChange("hasIrrigation", checked)
                  }
                  disabled={createMutation.isPending}
                />
                <Label htmlFor="hasIrrigation" className="cursor-pointer">
                  Has Irrigation System
                </Label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="hasClimateControl"
                  checked={formData.hasClimateControl}
                  onCheckedChange={(checked) =>
                    handleChange("hasClimateControl", checked)
                  }
                  disabled={createMutation.isPending}
                />
                <Label htmlFor="hasClimateControl" className="cursor-pointer">
                  Has Climate Control
                </Label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="hasAutomation"
                  checked={formData.hasAutomation}
                  onCheckedChange={(checked) =>
                    handleChange("hasAutomation", checked)
                  }
                  disabled={createMutation.isPending}
                />
                <Label htmlFor="hasAutomation" className="cursor-pointer">
                  Has Automation
                </Label>
              </div>
            </div>
          </div>

          {/* Manager & Date */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Management</h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 [&>*]:min-w-0">
              <UserSearchSelect
                selectedUserId={formData.managerUserId}
                selectedUserName={managerName}
                onSelect={(user) => {
                  handleChange("managerUserId", user?.id || "");
                  setManagerName(
                    user ? `${user.firstName} ${user.otherNames}` : ""
                  );
                }}
                disabled={createMutation.isPending}
                label="Manager"
                required
              />

              <Field>
                <FieldLabel htmlFor="commissionedDate">
                  Commissioned Date <span className="text-red-500">*</span>
                </FieldLabel>
                <DatePicker
                  id="commissionedDate"
                  value={formData.commissionedDate}
                  onChange={(value) => handleChange("commissionedDate", value)}
                  required
                  disabled={createMutation.isPending}
                />
                {fieldErrors.commissionedDate && (
                  <div className="text-red-500 text-xs mt-1">
                    {fieldErrors.commissionedDate.join(", ")}
                  </div>
                )}
              </Field>
            </div>
          </div>

          <Button
            type="submit"
            className="w-full"
            disabled={createMutation.isPending}
          >
            {createMutation.isPending ? "Creating..." : "Create Greenhouse"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
